import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { updatePurohitProfile } from "../../services/api";
import { toast } from "react-hot-toast";
import {
  Crown,
  Camera,
  Phone,
  Mail,
  MapPin,
  Check,
  User,
  Home,
  IndianRupee,
  X,
  Upload,
} from "lucide-react";

import {
  STATES,
  SPECIALIZATIONS,
  LANGUAGES,
  SERVICES,
  regex,
} from "./PurohitConfig";

const PurohitEdit = ({ onClose }) => {
  const navigate = useNavigate();
  const {
    auth,
    getCurrentUser,
    purohitProfile,
    updatePurohitProfile: updateContext,
  } = useAuth();

  const [data, setData] = useState({
    fullName: "",
    email: "",
    phone: "",
    altPhone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    profilePhotoUrl: "",
    experienceYears: "",
    specializations: [],
    languages: [],
    biography: "",
    achievements: "",
    servicesOffered: [],
    minPrice: "",
    maxPrice: "",
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ensure scrolling is enabled when component mounts
  useEffect(() => {
    document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // ✅ FIXED: Load existing profile data from AuthContext
  useEffect(() => {
    const currentUser = getCurrentUser();

    if (!currentUser) {
      console.log("No user logged in");
      toast.error("Please login to edit your profile.");
      onClose && onClose();
      return;
    }

    console.log("Loading profile for editing:", currentUser);

    // Load from current user (works for both purohitProfile in context or user object)
    const profileData = purohitProfile || currentUser;

    const loadedData = {
      fullName: profileData.fullName || "",
      email: profileData.email || "",
      phone: profileData.phone || "",
      altPhone: profileData.altPhone || "",
      address: profileData.address || "",
      city: profileData.city || "",
      state: profileData.state || "",
      pincode: profileData.pincode || "",
      profilePhotoUrl: profileData.profilePhotoUrl || "",
      experienceYears: String(profileData.experienceYears || ""),
      specializations: Array.isArray(profileData.specializations)
        ? profileData.specializations
        : [],
      languages: Array.isArray(profileData.languages)
        ? profileData.languages
        : [],
      biography: profileData.biography || "",
      achievements: profileData.achievements || "",
      servicesOffered: Array.isArray(profileData.servicesOffered)
        ? profileData.servicesOffered
        : [],
      minPrice: String(profileData.minPrice || ""),
      maxPrice: String(profileData.maxPrice || ""),
    };

    setData(loadedData);

    if (loadedData.profilePhotoUrl) {
      setPhotoPreview(loadedData.profilePhotoUrl);
    }

    // Check for saved step
    const savedStep = sessionStorage.getItem("editStep");
    if (savedStep) setStep(Number(savedStep));
  }, [getCurrentUser, purohitProfile, onClose]);

  // Sync to sessionStorage on data changes (for recovery)
  useEffect(() => {
    try {
      sessionStorage.setItem("purohitEditData", JSON.stringify(data));
    } catch (e) {
      console.error("Failed to save to sessionStorage", e);
    }
  }, [data]);

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (
      [
        "phone",
        "altPhone",
        "pincode",
        "experienceYears",
        "minPrice",
        "maxPrice",
      ].includes(name)
    ) {
      value = value.replace(/[^0-9]/g, "");
    }

    if (name === "experienceYears") value = value.slice(0, 2);
    if (name === "phone" || name === "altPhone") value = value.slice(0, 10);
    if (name === "pincode") value = value.slice(0, 6);
    if (name === "biography" || name === "achievements")
      value = value.slice(0, 500);

    setData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxChange = (key, item) => {
    const list = new Set(data[key] || []);
    if (list.has(item)) list.delete(item);
    else list.add(item);

    setData((prev) => ({ ...prev, [key]: Array.from(list) }));

    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/^image\/(jpe?g|png|webp)$/i)) {
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "Only JPG, JPEG, PNG, WEBP are allowed.",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "File size should be less than 5MB.",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const imgUrl = ev.target.result;
      setPhotoPreview(imgUrl);
      setData((prev) => ({ ...prev, profilePhotoUrl: imgUrl }));
    };
    reader.readAsDataURL(file);

    setErrors((prev) => ({ ...prev, profilePhoto: "" }));
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setData((prev) => ({ ...prev, profilePhotoUrl: "" }));
  };

  const validateStep1 = () => {
    const d = data;
    const nes = {};

    // Validate personal info fields
    if (!d.fullName || d.fullName.trim().length < 3)
      nes.fullName = "Full name is required (min 3 characters).";
    if (!d.phone || !regex.phone.test(d.phone))
      nes.phone = "Enter a valid 10-digit phone number.";
    if (d.altPhone && !regex.phone.test(d.altPhone))
      nes.altPhone = "Enter a valid 10-digit phone number.";
    if (!d.address || d.address.trim().length < 5)
      nes.address = "Address is required (min 5 characters).";
    if (!d.city || d.city.trim().length < 2) nes.city = "City is required.";
    if (!d.state) nes.state = "State is required.";
    if (!d.pincode || !regex.pincode.test(d.pincode))
      nes.pincode = "Enter a valid 6-digit pincode.";

    // Validate professional fields
    if (!d.experienceYears || !regex.experience.test(d.experienceYears))
      nes.experienceYears = "Enter valid years of experience.";
    if (!d.specializations?.length)
      nes.specializations = "Select at least one specialization.";
    if (!d.languages?.length) nes.languages = "Select at least one language.";

    setErrors(nes);
    return !Object.keys(nes).length;
  };

  const validateStep2 = () => {
    const d = data;
    const nes = {};

    if (!d.biography || d.biography.trim().length < 20)
      nes.biography = "Biography should be at least 20 characters.";
    if (!d.servicesOffered?.length)
      nes.servicesOffered = "Select at least one service.";
    if (!d.minPrice || !regex.price.test(d.minPrice))
      nes.minPrice = "Enter a valid minimum price.";
    if (!d.maxPrice || !regex.price.test(d.maxPrice))
      nes.maxPrice = "Enter a valid maximum price.";
    if (d.minPrice && d.maxPrice && Number(d.minPrice) > Number(d.maxPrice)) {
      nes.maxPrice = "Maximum must be greater than minimum.";
    }

    setErrors(nes);
    return !Object.keys(nes).length;
  };

  const goNext = () => {
    if (step === 1) {
      if (!validateStep1()) return;

      sessionStorage.setItem("editStep", "2");
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (step === 2) {
      if (!validateStep2()) return;

      saveProfile();
    }
  };

  const saveProfile = async () => {
    setIsSubmitting(true);

    try {
      // Prepare updated profile data - match backend schema
      const profileData = {
        // Personal info - ALL EDITABLE
        fullName: data.fullName.trim(),
        email: data.email.toLowerCase().trim(),
        phone: data.phone.trim(),
        altPhone: data.altPhone ? data.altPhone.trim() : undefined,

        // Address info - ALL EDITABLE
        address: data.address.trim(),
        city: data.city.trim(),
        state: data.state,
        pincode: data.pincode.trim(),

        // Experience & skills
        experienceYears: Number(data.experienceYears),
        specializations: data.specializations || [],
        languages: data.languages || [],

        // Bio & services
        biography: data.biography.trim(),
        achievements: data.achievements ? data.achievements.trim() : undefined,
        servicesOffered: data.servicesOffered || [],

        // Pricing
        minPrice: Number(data.minPrice),
        maxPrice: Number(data.maxPrice),

        // Optional
        profilePhotoUrl: data.profilePhotoUrl || undefined,
        isComplete: true,
      };

      // Remove undefined fields
      Object.keys(profileData).forEach((key) => {
        if (profileData[key] === undefined) {
          delete profileData[key];
        }
      });

      console.log("Updating profile with data:", profileData);

      // Update via API
      const response = await updatePurohitProfile(profileData);

      console.log("Profile update response:", response.data);

      // Update context with updated profile
      updateContext(response.data.profile || response.data);

      // Clear edit session data
      sessionStorage.removeItem("editStep");
      sessionStorage.removeItem("purohitEditData");

      toast.success("Profile updated successfully!", {
        duration: 3000,
      });

      // Show success modal
      setShowSuccess(true);
    } catch (error) {
      console.error("Profile update error:", error);
      console.error("Error response:", error.response);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update profile. Please try again.";

      toast.error(errorMessage, {
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (step === 2) {
      sessionStorage.setItem("editStep", "1");
      setStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (step === 1) {
      onClose();
    }
  };

  const closeSuccess = () => {
    setShowSuccess(false);
    navigate("/pandith-parichaya");
    onClose && onClose();
  };

  const StepCircle = ({ index }) => {
    const status =
      step === index ? "current" : step > index ? "done" : "upcoming";

    const base =
      "flex items-center justify-center w-9 h-9 rounded-full text-xs font-semibold border transition";

    let classes = "";
    let content = index;

    if (status === "current") {
      classes =
        "bg-gradient-to-b from-orange-500 to-red-500 text-white border-transparent shadow-md";
    } else if (status === "done") {
      classes = "bg-green-500 text-white border-green-600 shadow-sm";
      content = <Check size={16} strokeWidth={3} />;
    } else {
      classes = "bg-gray-100 text-gray-400 border-gray-200";
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <div className={base + " " + classes}>{content}</div>
        <span
          className={`text-[11px] ${
            status === "upcoming" ? "text-gray-400" : "text-gray-700"
          }`}
        >
          {index === 1 ? "Profile & Expertise" : "Services"}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="max-w-6xl mx-auto px-3 md:px-4 py-8 md:py-10">
        <div className="bg-white/95 rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.06)] border border-orange-300 mt-3 px-4 sm:px-6 md:px-8 py-7 md:py-8 max-w-4xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <div className="w-14 h-14 md:w-16 md:h-16 mx-auto rounded-full bg-gradient-to-b from-orange-500 to-red-500 flex items-center justify-center shadow-lg mb-3">
              <Crown size={28} className="text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              Edit Purohit Profile
            </h1>
            <p className="text-gray-700 text-sm">
              Update your profile information
            </p>
          </div>

          <div className="w-full overflow-x-auto pb-3 mb-6">
            <div className="flex justify-center items-center gap-4 min-w-max">
              {[1, 2].map((i) => (
                <React.Fragment key={i}>
                  <StepCircle index={i} />
                  {i < 2 && (
                    <div
                      className={`h-1 w-16 md:w-24 rounded-full transition-all ${
                        step > i
                          ? "bg-gradient-to-r from-orange-500 to-red-500"
                          : "bg-gray-300"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {step === 1 && (
            <div className="grid md:grid-cols-[1.15fr,1.4fr] gap-6 md:gap-7 items-start">
              {/* LEFT: Profile Photo Section - EDITABLE */}
              <div className="bg-gray-50 rounded-2xl p-5 sm:p-6 border border-orange-200 shadow-md">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-orange-400 bg-gradient-to-b from-orange-500 to-red-500 flex items-center justify-center">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Camera size={26} className="text-white opacity-90" />
                      )}
                    </div>

                    <label className="absolute -bottom-1 -right-1 bg-white shadow p-1.5 rounded-full cursor-pointer">
                      <Camera size={14} className="text-orange-600" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                    </label>
                  </div>
                </div>

                {errors.profilePhoto && (
                  <p className="text-[11px] text-red-500 text-center mb-1">
                    {errors.profilePhoto}
                  </p>
                )}

                {photoPreview && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="mx-auto mb-3 block text-[11px] text-red-700 underline"
                  >
                    Remove photo
                  </button>
                )}

                <p className="text-[10px] text-orange-900/80 text-center">
                  ✅ Profile photo is editable
                </p>

                {/* Personal Info - EDITABLE */}
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="fullName"
                      value={data.fullName}
                      readOnly
                      className={`w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed ${
                        errors.fullName ? "border-red-400" : "border-gray-200"
                      }`}
                    />

                    {errors.fullName && (
                      <p className="text-[10px] text-red-500 mt-0.5">
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      Email <span className="text-gray-400"></span>
                    </label>
                    <input
                      name="email"
                      value={data.email}
                      disabled
                      className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="phone"
                      value={data.phone}
                      readOnly
                      className={`w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed ${
                        errors.phone ? "border-red-400" : "border-gray-200"
                      }`}
                    />

                    {errors.phone && (
                      <p className="text-[10px] text-red-500 mt-0.5">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      Alt Phone
                    </label>
                    <input
                      name="altPhone"
                      value={data.altPhone}
                      onChange={handleChange}
                      className={`w-full rounded-lg border px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-orange-400 ${
                        errors.altPhone ? "border-red-400" : "border-gray-200"
                      }`}
                    />
                    {errors.altPhone && (
                      <p className="text-[10px] text-red-500 mt-0.5">
                        {errors.altPhone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="address"
                      value={data.address}
                      readOnly
                      className="w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                    />

                    {errors.address && (
                      <p className="text-[10px] text-red-500 mt-0.5">
                        {errors.address}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="city"
                        value={data.city}
                        readOnly
                        className="w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                      />

                      {errors.city && (
                        <p className="text-[10px] text-red-500 mt-0.5">
                          {errors.city}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium mb-1">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="pincode"
                        value={data.pincode}
                        readOnly
                        className="w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                      />

                      {errors.pincode && (
                        <p className="text-[10px] text-red-500 mt-0.5">
                          {errors.pincode}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="state"
                      value={data.state}
                      disabled
                      className="w-full rounded-lg border px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                    >
                      <option value="">Select State</option>
                      {STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="text-[10px] text-red-500 mt-0.5">
                        {errors.state}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT: Expertise form */}
              <div>
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
                  Qualifications &amp; Expertise
                </h2>

                <div className="space-y-4 text-sm">
                  <div className="max-w-xs">
                    <label className="block text-xs font-medium mb-1">
                      Years of Experience{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="experienceYears"
                      value={data.experienceYears}
                      onChange={handleChange}
                      placeholder="e.g., 10"
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                        errors.experienceYears
                          ? "border-red-400"
                          : "border-gray-200"
                      }`}
                    />
                    {errors.experienceYears && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.experienceYears}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="text-xs font-medium">
                        Specializations <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                      {SPECIALIZATIONS.map((spec) => (
                        <div
                          key={spec}
                          className="flex items-center gap-1.5 select-none"
                        >
                          <input
                            type="checkbox"
                            checked={data.specializations.includes(spec)}
                            onChange={() =>
                              handleCheckboxChange("specializations", spec)
                            }
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                          />
                          <span className="text-gray-800">{spec}</span>
                        </div>
                      ))}
                    </div>
                    {errors.specializations && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.specializations}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="text-xs font-medium">
                        Languages Known <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 text-[12px]">
                      {LANGUAGES.map((lang) => (
                        <div
                          key={lang}
                          className="flex items-center gap-1.5 select-none"
                        >
                          <input
                            type="checkbox"
                            checked={data.languages.includes(lang)}
                            onChange={() =>
                              handleCheckboxChange("languages", lang)
                            }
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                          />
                          <span className="text-gray-800">{lang}</span>
                        </div>
                      ))}
                    </div>
                    {errors.languages && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.languages}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 text-center md:text-left">
                Services &amp; Pricing
              </h2>

              <div className="grid md:grid-cols-[1.4fr,1.1fr] gap-6 md:gap-7 items-start">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">
                      Biography <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="biography"
                      value={data.biography}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Write a short introduction about your experience, style and how you serve devotees..."
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                        errors.biography ? "border-red-400" : "border-gray-200"
                      }`}
                    />
                    {errors.biography && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.biography}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1">
                      Achievements &amp; Recognition{" "}
                      <span className="text-gray-400 text-[10px]">
                        (optional)
                      </span>
                    </label>
                    <textarea
                      name="achievements"
                      value={data.achievements}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Mention any awards, temple experience or special recognition..."
                      className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-400 border-gray-200"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-[#F9FBFF] rounded-2xl p-4 border border-blue-100 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Services Offered
                      </h3>
                      <span className="text-[11px] text-gray-500">
                        Select all that apply
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                      {SERVICES.map((srv) => (
                        <div
                          key={srv}
                          className="flex items-center gap-1.5 select-none"
                        >
                          <input
                            type="checkbox"
                            checked={data.servicesOffered.includes(srv)}
                            onChange={() =>
                              handleCheckboxChange("servicesOffered", srv)
                            }
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                          />
                          <span className="text-gray-800">{srv}</span>
                        </div>
                      ))}
                    </div>
                    {errors.servicesOffered && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors.servicesOffered}
                      </p>
                    )}
                  </div>

                  <div className="bg-[#FFF4E8] rounded-2xl p-4 border border-orange-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                          <IndianRupee className="w-4 h-4 text-orange-600" />
                          Pricing Range
                        </p>
                        <p className="text-[11px] text-gray-600">
                          Show a realistic starting & maximum dakshina.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium mb-1">
                          Min Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="minPrice"
                          value={data.minPrice}
                          onChange={handleChange}
                          placeholder="2500"
                          className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                            errors.minPrice
                              ? "border-red-400"
                              : "border-gray-200"
                          }`}
                        />
                        {errors.minPrice && (
                          <p className="text-[11px] text-red-500 mt-1">
                            {errors.minPrice}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium mb-1">
                          Max Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="maxPrice"
                          value={data.maxPrice}
                          onChange={handleChange}
                          placeholder="15000"
                          className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400 ${
                            errors.maxPrice
                              ? "border-red-400"
                              : "border-gray-200"
                          }`}
                        />
                        {errors.maxPrice && (
                          <p className="text-[11px] text-red-500 mt-1">
                            {errors.maxPrice}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-[11px] text-gray-700 mt-3">
                      Shown to devotees as:{" "}
                      <span className="font-semibold text-orange-800">
                        ₹{data.minPrice || "—"} - ₹{data.maxPrice || "—"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-7 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>

            <button
              type="button"
              onClick={goNext}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="select-none">Saving...</span>
                </>
              ) : step === 2 ? (
                <>
                  <Check size={16} strokeWidth={2.5} />
                  <span className="select-none">Save Changes</span>
                </>
              ) : (
                <>
                  <span className="select-none">Next</span>
                  <span className="select-none">→</span>
                </>
              )}
            </button>
          </div>
        </div>

        {showSuccess && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-auto p-6 sm:px-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-500 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-lg font-semibold text-green-700 mb-1">
                Profile Updated Successfully!
              </h3>

              <p className="text-sm text-gray-600 mb-4">
                Your changes have been saved and will be visible to devotees.
              </p>

              <button
                type="button"
                onClick={closeSuccess}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-green-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-green-600"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurohitEdit;
